// API client for TutorCat authentication and data operations
import { User } from '@/types'
import { getSiteUrl, isProd } from '@/lib/env'

// Get API base URL
// Production: same origin (Caddy / Netlify). Dev: same origin — Vite proxies to Contabo host :3001.
// Override with VITE_API_URL (e.g. http://localhost:3001) if needed.
const API_BASE_URL = (() => {
  const fromEnv = import.meta.env.VITE_API_URL as string | undefined
  if (fromEnv && fromEnv.trim() && !fromEnv.includes('mathayomwatsing')) {
    // Strip trailing /.netlify/functions if someone pasted a full functions base
    return fromEnv.replace(/\/\.netlify\/functions\/?$/, '').replace(/\/$/, '')
  }
  if (isProd) {
    return typeof window !== 'undefined' ? window.location.origin : getSiteUrl()
  }
  // Vite + Contabo: relative / same-origin (see vite.config.mts proxy)
  return typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173'
})()

interface ApiResponse<T = any> {
  success: boolean
  data?: T
  error?: string
  message?: string
  rateLimited?: boolean
  retryAfter?: number
}

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      // Use Netlify functions path (dev server proxies to Contabo host)
      const url = `${this.baseUrl}/.netlify/functions${endpoint}`

      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        credentials: 'include', // Include cookies for authentication
      })

      let data: any = {}
      let responseText = ''
      try {
        responseText = await response.text()
        if (responseText && responseText.trim()) {
          data = JSON.parse(responseText)
        } else {
          data = { error: 'No response data' }
        }
      } catch (parseError) {
        console.error('API: Failed to parse response JSON:', parseError)
        console.error('API: Raw response was:', responseText)
        data = { error: 'Invalid response format' }
      }

      // Check for 429 (rate limited) - show specific rate limit message
      if (response.status === 429) {
        return {
          success: false,
          error: data.error || 'Too many attempts. Please wait before trying again.',
          rateLimited: true,
          retryAfter: data.retryAfter,
        }
      }

      // Admin 401: clear local session and redirect (no AuthContext hook here)
      if (response.status === 401 && endpoint.includes('admin')) {
        if (typeof window !== 'undefined') {
          console.warn('Admin token expired, logging out user')
          const cookieConsentData = localStorage.getItem('cookie_consent_data')
          localStorage.clear()
          if (cookieConsentData) {
            localStorage.setItem('cookie_consent_data', cookieConsentData)
          }
          window.location.href = '/'
        }

        return {
          success: false,
          error: 'Admin session expired. Logging out...',
        }
      }

      if (!response.ok) {
        return {
          success: false,
          error: data.error || `Request failed (${response.status})`,
        }
      }

      if (data && typeof data === 'object' && data.success === false) {
        return {
          success: false,
          error: data.error || data.message || 'Request failed',
          data: data as T,
        }
      }

      return {
        success: true,
        data: data as T,
        message: data.message,
      }
    } catch (error) {
      console.error('API: Network error:', error)
      console.error('API: Base URL:', this.baseUrl)
      console.error('API: Endpoint:', endpoint)
      return {
        success: false,
        error: 'Network error',
      }
    }
  }

  // Authentication methods
  async login(username: string, password: string): Promise<ApiResponse<{ user: User; adminToken?: string }>> {
    return this.request('/auth-login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    })
  }

  async sendOTP(
    email: string,
    type: 'login' | 'signup' | 'password_reset'
  ): Promise<ApiResponse> {
    return this.request('/auth-send-otp', {
      method: 'POST',
      body: JSON.stringify({ email, type }),
    })
  }

  async verifyOTP(
    email: string,
    code: string,
    type: 'login' | 'signup' | 'password_reset',
    firstName?: string,
    lastName?: string,
    username?: string,
    password?: string
  ): Promise<ApiResponse<{ user: User; adminToken?: string }>> {
    return this.request('/auth-verify-otp', {
      method: 'POST',
      body: JSON.stringify({
        email,
        code,
        type,
        firstName,
        lastName,
        username,
        password,
      }),
    })
  }

  async resetPassword(
    email: string,
    otp: string,
    newPassword: string
  ): Promise<ApiResponse> {
    return this.request('/auth-reset-password', {
      method: 'POST',
      body: JSON.stringify({ email, otp, newPassword }),
    })
  }

  async logout(): Promise<ApiResponse> {
    return this.request('/auth-logout', {
      method: 'POST',
    })
  }

  async getCurrentUser(): Promise<ApiResponse<{ user: User }>> {
    return this.request('/auth-me', {
      method: 'GET',
    })
  }

  // Lesson methods
  async getLesson(lessonId: string): Promise<ApiResponse<{ lesson: any }>> {
    return this.request(`/get-lesson?lessonId=${lessonId}`, {
      method: 'GET',
    })
  }

  // AI processing methods
  async speechToText(audioData: { audioUrl?: string; audioBlob?: string }): Promise<ApiResponse<{ transcription: string; confidence: number }>> {
    return this.request('/ai-speech-to-text', {
      method: 'POST',
      body: JSON.stringify(audioData),
    })
  }

  async getAIFeedback(data: {
    transcription: string
    prompt: string
    criteria: { grammar?: boolean; vocabulary?: boolean; pronunciation?: boolean }
  }): Promise<ApiResponse<{
    overall_score: number
    feedback: any
    corrections: any
  }>> {
    return this.request('/ai-feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async checkSimilarity(data: {
    targetText: string
    userText: string
    threshold?: number
  }): Promise<ApiResponse<{
    similarity: number
    passed: boolean
    feedback: string
    suggestions: string[]
  }>> {
    return this.request('/ai-similarity', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  // Submit complete lesson results
  async submitLessonResults(data: any): Promise<ApiResponse> {
    return this.request('/submit-lesson-results', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  async finalizeLesson(lessonId: string): Promise<ApiResponse> {
    return this.request('/finalize-lesson', {
      method: 'POST',
      body: JSON.stringify({ lessonId }),
    })
  }

  async advanceLevel(): Promise<ApiResponse<{ fromLevel?: string; toLevel?: string }>> {
    return this.request('/advance-level', {
      method: 'POST',
    })
  }

  async getUserProgress(): Promise<ApiResponse> {
    return this.request('/progress/user', {
      method: 'GET',
    })
  }

  async getDashboardData(): Promise<ApiResponse> {
    return this.request('/get-dashboard-data', {
      method: 'GET',
    })
  }

  async getStudentDashboard(): Promise<ApiResponse> {
    return this.request('/get-student-dashboard', { method: 'GET' })
  }

  async getStudentLesson(lessonId: string): Promise<ApiResponse> {
    return this.request(`/get-student-lesson?lessonId=${encodeURIComponent(lessonId)}`, {
      method: 'GET',
    })
  }

  async getStudentPriorCharacter(): Promise<ApiResponse> {
    return this.request('/get-student-prior-character', { method: 'GET' })
  }

  async submitStudentLessonActivity(payload: Record<string, unknown>): Promise<ApiResponse> {
    return this.request('/submit-student-lesson-activity', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async evaluateStudentWriting(payload: {
    prompt: string
    text: string
    studentLessonId?: string
    minWords?: number
    level?: string
    passScore?: number
  }): Promise<
    ApiResponse & {
      overall_score?: number
      max_score?: number
      passed?: boolean
      pass_score?: number
      word_count?: number
      addresses_prompt?: boolean
      categories?: Array<{ name: string; score: number; max_score: number; comments: string }>
      summary?: string
      min_words_rejected?: boolean
      min_words?: number
    }
  > {
    return this.request('/evaluate-student-writing', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async finalizeStudentLesson(studentLessonId: string): Promise<ApiResponse> {
    return this.request('/finalize-student-lesson', {
      method: 'POST',
      body: JSON.stringify({ studentLessonId }),
    })
  }

  async getLessonsByLevel(level: string): Promise<ApiResponse> {
    return this.request(`/get-lessons-by-level?level=${level}`, {
      method: 'GET',
    })
  }

  async getAchievements(): Promise<ApiResponse> {
    return this.request('/get-user-achievements', {
      method: 'GET',
    })
  }

  async getAdminLessons(): Promise<ApiResponse> {
    // Admin token is in HTTP cookie, sent automatically with credentials: 'include'
    return this.request('/admin-lessons', {
      method: 'GET'
    })
  }

  async getAdminEvaluationTests(): Promise<ApiResponse> {
    // Use adminApiRequest to ensure proper token handling
    const { adminApiRequest } = await import('@/utils/adminApi')
    
    try {
      const response = await adminApiRequest('/.netlify/functions/admin-evaluation', {
        method: 'GET'
      })
      
      if (!response.ok) {
        const errorText = await response.text()
        return {
          success: false,
          error: errorText || `HTTP error! status: ${response.status}`
        }
      }
      
      const data = await response.json()
      return {
        success: true,
        data: data
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch evaluation tests'
      }
    }
  }
}

// Create and export API client instance
export const apiClient = new ApiClient(API_BASE_URL)
export default apiClient

/** Thin fetch with credentials — prefer apiClient methods when available. */
export async function makeAuthenticatedRequest(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  return fetch(url, {
    method: 'GET',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    credentials: 'include',
  })
}
